import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import {
  Container,
  HStack,
  LoadingPlaceholder,
  ProductCard,
  theme,
  useResponsive,
} from '@sg/garnish';

import { useDietaryRestrictions } from '@order/components';
import { useNavigateToProductDetails } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import type { MenuTabStackParamList } from '../../../../navigation/AppNavigation.props';
import type { MenuCollectionQuery } from '../../GraphQL';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuCollectionProductsGrid = (
  props: MenuCollectionProductsStackProps,
) => {
  const { products, isLoading } = props;
  const { match, minWidth } = useResponsive();

  const cardsPerRow = match([1, 2, 3, 4]);
  const containerStyle = minWidth.isSM && styles.containerSM;

  return (
    <Container style={containerStyle}>
      <HStack itemsPerRow={cardsPerRow} style={styles.stack}>
        {isLoading
          ? Array.from({ length: cardsPerRow }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <LoadingPlaceholder key={index} palette="cream" rowHeight={480} />
            ))
          : products.map((product) => (
              <MenuCollectionProductsGridItem
                key={product.id}
                product={product}
              />
            ))}
      </HStack>
    </Container>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const MenuCollectionProductsGridItem = (
  props: MenuCollectionProductsStackItemProps,
) => {
  const { product } = props;
  const { name, description, cost, calories, asset, dietaryProperties, label } =
    product;

  const { params } =
    useRoute<RouteProp<MenuTabStackParamList, 'MenuCollection'>>();
  const { restaurantSlug, addressId } = params;
  const navigateToProductDetails = useNavigateToProductDetails({
    restaurantSlug,
    deliveryAddressId: addressId,
  });
  const { formatPrice, formatNumber, t } = useLocalizationContext();
  const getRestrictionNotice = useDietaryRestrictions();

  // ─── HELPERS ─────────────────────────────────────────────────────

  const onCardPress = useCallback(() => {
    navigateToProductDetails(product);
  }, [navigateToProductDetails, product]);

  // ─────────────────────────────────────────────────────────────────

  const labelName = label?.name ?? '';
  const formattedPrice = formatPrice(cost, 'USD');
  const formattedCalories = `${formatNumber(calories ?? 0)} cal`;
  const imageSrc = asset?.url ?? '';

  const accessibilityLabel = t('menu.product-card.a11y.label', {
    productName: name,
    description,
    price: formattedPrice,
    extra: formattedCalories,
  });
  const accessibilityHint = t('menu.product-card.a11y.hint', {
    productName: name,
  });

  // ─────────────────────────────────────────────────────────────────

  return (
    <ProductCard
      restrictionNotice={getRestrictionNotice(dietaryProperties)}
      name={name}
      description={description}
      label={labelName}
      price={formattedPrice}
      calories={formattedCalories}
      cloudinaryImageSrc={imageSrc}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      onCardPress={onCardPress}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    paddingVertical: theme.spacing['10'],
  },
  stack: {
    paddingVertical: theme.spacing['6'],
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type MenuCollectionProductsStackProps = {
  readonly products: MenuCollectionQuery['menuCollection']['products'];
  readonly isLoading: boolean;
};

type MenuCollectionProductsStackItemProps = {
  readonly product: MenuCollectionQuery['menuCollection']['products'][number];
};
